/**
 * Accent paints for a step card. Every class is a full literal so Tailwind
 * keeps it. No tone = the original primary card.
 * For Media Devoted | Created By Ciro Autiero
 */

export const STEP_TONES = ['sky', 'violet', 'amber', 'emerald', 'rose', 'teal', 'fuchsia'] as const;

export type StepTone = (typeof STEP_TONES)[number];

export interface StepToneClasses {
  cardOpen: string;
  cardIdle: string;
  launch: string;
  badgeOpen: string;
  badgeDone: string;
  badgeIdle: string;
  icon: string;
  body: string;
}

const DEFAULT_STEP_TONE: StepToneClasses = {
  cardOpen: 'border-primary/40 shadow-sm',
  cardIdle: 'border-border',
  launch: 'transition-shadow hover:border-primary/40 hover:shadow-sm',
  badgeOpen: 'bg-primary text-primary-foreground',
  badgeDone: 'bg-primary/15 text-primary',
  badgeIdle: 'bg-muted text-muted-foreground',
  icon: 'text-muted-foreground',
  body: 'border-border/60',
};

export const STEP_TONE: Record<StepTone, StepToneClasses> = {
  sky: {
    cardOpen: 'border-sky-500/50 shadow-sm',
    cardIdle: 'border-sky-500/30',
    launch: 'transition-shadow hover:border-sky-500/50 hover:shadow-sm',
    badgeOpen: 'bg-sky-600 text-white',
    badgeDone: 'bg-sky-500/15 text-sky-800 dark:text-sky-200',
    badgeIdle: 'bg-sky-500/15 text-sky-800 dark:text-sky-200',
    icon: 'text-sky-600 dark:text-sky-400',
    body: 'border-sky-500/25',
  },
  violet: {
    cardOpen: 'border-violet-500/50 shadow-sm',
    cardIdle: 'border-violet-500/30',
    launch: 'transition-shadow hover:border-violet-500/50 hover:shadow-sm',
    badgeOpen: 'bg-violet-600 text-white',
    badgeDone: 'bg-violet-500/15 text-violet-800 dark:text-violet-200',
    badgeIdle: 'bg-violet-500/15 text-violet-800 dark:text-violet-200',
    icon: 'text-violet-600 dark:text-violet-400',
    body: 'border-violet-500/25',
  },
  amber: {
    cardOpen: 'border-amber-500/50 shadow-sm',
    cardIdle: 'border-amber-500/30',
    launch: 'transition-shadow hover:border-amber-500/50 hover:shadow-sm',
    badgeOpen: 'bg-amber-600 text-white',
    badgeDone: 'bg-amber-500/15 text-amber-800 dark:text-amber-200',
    badgeIdle: 'bg-amber-500/15 text-amber-800 dark:text-amber-200',
    icon: 'text-amber-600 dark:text-amber-400',
    body: 'border-amber-500/25',
  },
  emerald: {
    cardOpen: 'border-emerald-500/50 shadow-sm',
    cardIdle: 'border-emerald-500/30',
    launch: 'transition-shadow hover:border-emerald-500/50 hover:shadow-sm',
    badgeOpen: 'bg-emerald-600 text-white',
    badgeDone: 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-200',
    badgeIdle: 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-200',
    icon: 'text-emerald-600 dark:text-emerald-400',
    body: 'border-emerald-500/25',
  },
  rose: {
    cardOpen: 'border-rose-500/50 shadow-sm',
    cardIdle: 'border-rose-500/30',
    launch: 'transition-shadow hover:border-rose-500/50 hover:shadow-sm',
    badgeOpen: 'bg-rose-600 text-white',
    badgeDone: 'bg-rose-500/15 text-rose-800 dark:text-rose-200',
    badgeIdle: 'bg-rose-500/15 text-rose-800 dark:text-rose-200',
    icon: 'text-rose-600 dark:text-rose-400',
    body: 'border-rose-500/25',
  },
  teal: {
    cardOpen: 'border-teal-500/50 shadow-sm',
    cardIdle: 'border-teal-500/30',
    launch: 'transition-shadow hover:border-teal-500/50 hover:shadow-sm',
    badgeOpen: 'bg-teal-600 text-white',
    badgeDone: 'bg-teal-500/15 text-teal-800 dark:text-teal-200',
    badgeIdle: 'bg-teal-500/15 text-teal-800 dark:text-teal-200',
    icon: 'text-teal-600 dark:text-teal-400',
    body: 'border-teal-500/25',
  },
  fuchsia: {
    cardOpen: 'border-fuchsia-500/50 shadow-sm',
    cardIdle: 'border-fuchsia-500/30',
    launch: 'transition-shadow hover:border-fuchsia-500/50 hover:shadow-sm',
    badgeOpen: 'bg-fuchsia-600 text-white',
    badgeDone: 'bg-fuchsia-500/15 text-fuchsia-800 dark:text-fuchsia-200',
    badgeIdle: 'bg-fuchsia-500/15 text-fuchsia-800 dark:text-fuchsia-200',
    icon: 'text-fuchsia-600 dark:text-fuchsia-400',
    body: 'border-fuchsia-500/25',
  },
};

export function stepTone(tone?: StepTone): StepToneClasses {
  if (!tone) return DEFAULT_STEP_TONE;
  return STEP_TONE[tone];
}
